import { useEffect, useState } from "react";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentUser } from "@/hooks/platform/useCurrentUser";

export type PresenceStatus = "online" | "away" | "offline";

const CHANNEL = "platform-presence";

let channel: RealtimeChannel | null = null;
let online = new Set<string>();
const listeners = new Set<(ids: Set<string>) => void>();

function emit() {
  listeners.forEach((fn) => fn(new Set(online)));
}

/** Joins (once) the shared presence channel so every screen reports availability. */
function ensureChannel(userId: string | null) {
  if (channel || !userId) return;
  channel = supabase.channel(CHANNEL, { config: { presence: { key: userId } } });
  channel
    .on("presence", { event: "sync" }, () => {
      const state = channel?.presenceState() ?? {};
      online = new Set(Object.keys(state));
      emit();
    })
    .subscribe(async (status) => {
      if (status === "SUBSCRIBED") {
        await channel?.track({ userId, at: new Date().toISOString() });
      }
    });
}

/** Publishes my availability and keeps `last_active_at` fresh. */
export function usePresenceBroadcast() {
  const { userId } = useCurrentUser();

  useEffect(() => {
    if (!userId) return;
    ensureChannel(userId);

    const beat = async () => {
      await supabase.from("profiles").update({ last_active_at: new Date().toISOString() }).eq("id", userId);
    };
    void beat();
    const id = setInterval(beat, 120000);

    return () => {
      clearInterval(id);
      if (channel) {
        void channel.untrack();
        supabase.removeChannel(channel);
        channel = null;
        online = new Set();
        emit();
      }
    };
  }, [userId]);
}

/** Live set of member ids currently connected to the platform. */
export function useOnlineUsers() {
  const [ids, setIds] = useState<Set<string>>(() => new Set(online));
  useEffect(() => {
    listeners.add(setIds);
    setIds(new Set(online));
    return () => { listeners.delete(setIds); };
  }, []);
  return ids;
}

/** Availability for one member: live presence first, then recent activity. */
export function usePresenceStatus(userId: string | null | undefined) {
  const ids = useOnlineUsers();
  const [lastActive, setLastActive] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) return;
    let active = true;
    void supabase
      .from("profiles")
      .select("last_active_at")
      .eq("id", userId)
      .maybeSingle()
      .then(({ data }) => { if (active) setLastActive((data as { last_active_at?: string } | null)?.last_active_at ?? null); });
    return () => { active = false; };
  }, [userId]);

  if (!userId) return { status: "offline" as PresenceStatus, lastActive: null, reachable: false };

  let status: PresenceStatus = "offline";
  if (ids.has(userId)) status = "online";
  else if (lastActive && Date.now() - new Date(lastActive).getTime() < 15 * 60 * 1000) status = "away";

  return { status, lastActive, reachable: status === "online" };
}

export const presenceLabel = (status: PresenceStatus) =>
  status === "online" ? "Available now" : status === "away" ? "Recently active" : "Offline";

export const presenceDotClass = (status: PresenceStatus) =>
  status === "online" ? "bg-emerald-500" : status === "away" ? "bg-amber-500" : "bg-muted-foreground/50";
